// VB-024B.3 — Visitor → FREE account migration service
//
// Reads local visitor data (vb-visitor-canvas + vb-visitor-entities), persists
// entities to real Supabase tables, remaps canvas node entityIds to the new
// Supabase-issued UUIDs, saves the canvas snapshot for the connected user, then
// purges the visitor localStorage keys — but ONLY if every entity migrated
// successfully (conservative: preserves local data on any failure).
//
// Called once after the first successful login when visitor data is detected.
// Anti-double-migration: the caller (App.tsx) gates on a useRef flag that is
// set before the async call; since the keys are purged on success, subsequent
// logins with no visitor data are a no-op.

import { canvasService } from './canvas.service';
import { supabase } from '../lib/supabase';
import { taskService } from './task.service';
import { noteService } from './note.service';
import { goalService } from './goal.service';
import type { Task, Note, Goal, CanvasNodeData } from '../types';
import type { Node, Edge } from '@xyflow/react';

// — Storage keys (must match those used in App.tsx / useAuthStore.ts) ——————
export const VISITOR_CANVAS_KEY = 'vb-visitor-canvas';
export const VISITOR_ENTITIES_KEY = 'vb-visitor-entities';
export const VISITOR_MODE_KEY = 'vb-visitor-mode';

// ————————————————————————————————————————————————————————————————————————————

export interface VisitorMigrationResult {
  success: boolean;
  migratedGoals: number;
  migratedTasks: number;
  migratedNotes: number;
}

/** Returns true if at least one visitor localStorage key has content. */
export function hasVisitorDataToMigrate(): boolean {
  return !!(localStorage.getItem(VISITOR_CANVAS_KEY) || localStorage.getItem(VISITOR_ENTITIES_KEY));
}

/**
 * Migrates visitor-local data to the authenticated user's Supabase account.
 *
 * Migration order: goals → tasks → notes → canvas remap → canvas save → purge.
 *
 * Safety rules:
 * - keys are purged ONLY if every entity migrated without error
 * - canvas is saved ONLY if (a) all entities migrated and (b) the account has
 *   no pre-existing canvas snapshot (avoids overwriting an existing user canvas)
 * - on any single entity failure the whole migration is considered failed:
 *   visitor keys are preserved intact so the user's data is never silently lost
 */
export async function migrateVisitorData(userId: string): Promise<VisitorMigrationResult> {
  // ── 1. Read raw visitor data from localStorage ──────────────────────────
  let localEntities: { tasks?: Task[]; notes?: Note[]; goals?: Goal[] } = {};
  let localCanvas: { nodes?: Node<CanvasNodeData>[]; edges?: Edge[] } = {};

  try {
    const raw = localStorage.getItem(VISITOR_ENTITIES_KEY);
    if (raw) localEntities = JSON.parse(raw);
  } catch {
    /* malformed JSON — treat as empty */
  }

  try {
    const raw = localStorage.getItem(VISITOR_CANVAS_KEY);
    if (raw) localCanvas = JSON.parse(raw);
  } catch {
    /* malformed JSON — treat as empty */
  }

  const localGoals: Goal[] = Array.isArray(localEntities.goals) ? localEntities.goals : [];
  const localTasks: Task[] = Array.isArray(localEntities.tasks) ? localEntities.tasks : [];
  const localNotes: Note[] = Array.isArray(localEntities.notes) ? localEntities.notes : [];
  const localNodes: Node<CanvasNodeData>[] = Array.isArray(localCanvas.nodes)
    ? localCanvas.nodes
    : [];
  const localEdges: Edge[] = Array.isArray(localCanvas.edges) ? localCanvas.edges : [];

  // Nothing to do — purge any orphan keys and return
  if (
    localGoals.length === 0 &&
    localTasks.length === 0 &&
    localNotes.length === 0 &&
    localNodes.length === 0
  ) {
    purgeVisitorKeys();
    return { success: true, migratedGoals: 0, migratedTasks: 0, migratedNotes: 0 };
  }

  // ── 2. Check whether the account already has a canvas snapshot ───────────
  // If it does, we will NOT overwrite it (conservative merge: only add entities).
  let hasExistingCanvas = false;
  try {
    const existing = await canvasService.loadCanvas(userId);
    hasExistingCanvas = !!(existing && existing.nodes.length > 0);
  } catch {
    /* assume no pre-existing canvas on error */
  }

  // ── 3. Migrate entities (goals first — tasks/notes may reference goal IDs) ─
  // oldLocalId → newSupabaseId
  const entityIdMap = new Map<string, string>();

  let migratedGoals = 0;
  let migratedTasks = 0;
  let migratedNotes = 0;
  let anyFailed = false;

  // ── Goals ────────────────────────────────────────────────────────────────
  for (const goal of localGoals) {
    try {
      const persisted = await goalService.createGoal({
        title: goal.title,
        description: goal.description,
        status: goal.status,
        domainId: goal.domainId, // safeUUID inside service maps mock IDs (d1…d4) → null
        tags: goal.tags || [],
      });
      entityIdMap.set(goal.id, persisted.id);
      migratedGoals++;
    } catch (err) {
      console.error('[visitor.migration] goal failed:', err);
      anyFailed = true;
    }
  }

  // ── Tasks (goalId remapped if goal was successfully migrated) ────────────
  for (const task of localTasks) {
    try {
      const remappedGoalId = task.goalId ? (entityIdMap.get(task.goalId) ?? undefined) : undefined;

      const persisted = await taskService.createTask({
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        domainId: task.domainId,
        dueDate: task.dueDate,
        tags: task.tags || [],
        goalId: remappedGoalId,
        // eventId and parentId are not reachable in visitor mode
      });
      entityIdMap.set(task.id, persisted.id);
      migratedTasks++;
    } catch (err) {
      console.error('[visitor.migration] task failed:', err);
      anyFailed = true;
    }
  }

  // ── Notes (goalId + taskId remapped) ────────────────────────────────────
  for (const note of localNotes) {
    try {
      const remappedGoalId = note.goalId ? (entityIdMap.get(note.goalId) ?? undefined) : undefined;
      const remappedTaskId = note.taskId ? (entityIdMap.get(note.taskId) ?? undefined) : undefined;

      const persisted = await noteService.createNote({
        title: note.title,
        content: note.content,
        domainId: note.domainId,
        tags: note.tags || [],
        goalId: remappedGoalId,
        taskId: remappedTaskId,
      });
      entityIdMap.set(note.id, persisted.id);
      migratedNotes++;
    } catch (err) {
      console.error('[visitor.migration] note failed:', err);
      anyFailed = true;
    }
  }

  // ── 4. If any entity failed — conserve visitor data, return failure ──────
  if (anyFailed) {
    // Do NOT purge: visitor keys remain intact so data is never silently lost.
    return { success: false, migratedGoals, migratedTasks, migratedNotes };
  }

  // ── 5. Remap canvas node entityIds to new Supabase IDs ───────────────────
  const remappedNodes = localNodes.map((node) => {
    const oldId = node.data?.entityId as string | undefined;
    if (!oldId) return node; // freeNode, zoneNode — no entityId to remap
    const newId = entityIdMap.get(oldId);
    if (!newId) return node; // unknown ID (e.g. example node not in entities list)
    return { ...node, data: { ...node.data, entityId: newId } };
  });

  // ── 6. Save canvas to Supabase (only if no prior canvas on this account) ─
  if (!hasExistingCanvas && (remappedNodes.length > 0 || localEdges.length > 0)) {
    try {
      // Resolve the default brain for this user
      const { data: defaultBrain } = await supabase
        .from('visual_brain_canvases')
        .select('id')
        .eq('user_id', userId)
        .eq('is_default', true)
        .single();
      if (defaultBrain) {
        await canvasService.saveCanvas(userId, defaultBrain.id, remappedNodes, localEdges);
      }
    } catch (err) {
      console.error('[visitor.migration] canvas save failed:', err);
      // Canvas save failed — conserve visitor keys, return failure
      return { success: false, migratedGoals, migratedTasks, migratedNotes };
    }
  }

  // ── 7. Purge visitor keys only after full success ─────────────────────────
  purgeVisitorKeys();

  return { success: true, migratedGoals, migratedTasks, migratedNotes };
}

/**
 * Purges all visitor localStorage keys without migrating any data.
 * Call this on the LOGIN branch: the user's existing account is untouched,
 * visitor data is discarded cleanly after successful authentication.
 */
export function purgeVisitorKeys(): void {
  localStorage.removeItem(VISITOR_CANVAS_KEY);
  localStorage.removeItem(VISITOR_ENTITIES_KEY);
  localStorage.removeItem(VISITOR_MODE_KEY);
}
